// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { InstrumentSpecificType } from './InstrumentSpecificType';
export class _InstrumentLookupComparer {
    constructor() {
        this.sortInstrumentList = (instrumentList, params) => {
            instrumentList.sort((x, y) => this.compareInstruments(x, y, params));
            return instrumentList;
        };
        this.sortOptionList = (instrumentList, pattern) => {
            instrumentList.sort((x, y) => this.compareByPattern((pattern !== null && pattern !== void 0 ? pattern : '').toLowerCase(), x, y));
            return instrumentList;
        };
        this.compareInstruments = (x, y, params) => {
            var _a;
            let result = 0;
            if (params.isSortByInstrumentType) {
                result = this.compareInstrumentsTypesInternal(x, y);
            }
            if (result === 0) {
                return this.compareInstrumentsInternal(((_a = params.pattern) !== null && _a !== void 0 ? _a : '').toLowerCase(), x, y);
            }
            return result;
        };
    }
    compareInstrumentsTypesInternal(x, y) {
        // By instrument type only options drop to the bottom
        const aIsOptionSymbol = x.isOptionSymbol;
        const bIsOptionSymbol = y.isOptionSymbol;
        return Number(aIsOptionSymbol) - Number(bIsOptionSymbol);
    }
    compareInstrumentsInternal(pattern, x, y) {
        const aIsOptionSymbol = x.isOptionSymbol;
        const bIsOptionSymbol = y.isOptionSymbol;
        const aIsFutureSymbol = x.isFuturesSymbol;
        const bIsFutureSymbol = y.isFuturesSymbol;
        if (aIsOptionSymbol && bIsOptionSymbol) {
            return this.compareOptionsInternal(pattern, x, y);
        }
        else if (aIsFutureSymbol && bIsFutureSymbol) {
            return this.compareFuturesInternal(x, y);
        }
        else {
            return this.compareByPattern(pattern, x, y);
        }
    }
    compareOptionsInternal(pattern, x, y) {
        let result = 0;
        if (x.ForwardBaseInstrument != null && y.ForwardBaseInstrument != null) {
            result = this.getInstrumentDisplayName(x.ForwardBaseInstrument).localeCompare(this.getInstrumentDisplayName(y.ForwardBaseInstrument));
            if (result !== 0) {
                return this.compareByPattern(pattern, x, y);
            }
        }
        if (result === 0 && x.ExpDate != null && y.ExpDate != null) {
            result = x.ExpDate.getTime() - y.ExpDate.getTime();
        }
        if (result === 0) {
            result = x.StrikePrice - y.StrikePrice;
        }
        if (result === 0) {
            result = x.PutCall - y.PutCall;
        }
        if (result === 0) {
            const xStrikeVersion = isValidString(x.StrikeVersion) ? x.StrikeVersion : '';
            const yStrikeVersion = isValidString(y.StrikeVersion) ? y.StrikeVersion : '';
            result = xStrikeVersion.localeCompare(yStrikeVersion);
        }
        return result;
    }
    compareFuturesInternal(x, y) {
        var _a;
        let result = ((_a = x.SourceName) === null || _a === void 0 ? void 0 : _a.localeCompare(y.SourceName || '')) || 0;
        if (result === 0) {
            result = Number(x.InstrumentSpecificType) - Number(y.InstrumentSpecificType);
        }
        if (result === 0 && x.ExpDate != null && y.ExpDate != null) {
            result = x.ExpDate.getTime() - y.ExpDate.getTime();
        }
        return result;
    }
    compareByPattern(pattern, x, y) {
        const aDisplayName = this.getInstrumentDisplayName(x);
        const bDisplayName = this.getInstrumentDisplayName(y);
        if (pattern !== '') {
            const aDescription = this.getInstrumentDescription(x);
            const bDescription = this.getInstrumentDescription(y);
            let compareResult = this.tryCompareByFullMatch(pattern, aDisplayName, bDisplayName);
            if (compareResult.success) {
                return compareResult.result;
            }
            compareResult = this.tryCompareByPartialMatch(pattern, aDisplayName, bDisplayName);
            if (compareResult.success) {
                return compareResult.result;
            }
            compareResult = this.tryCompareByFullMatch(pattern, aDescription, bDescription);
            if (compareResult.success) {
                return compareResult.result;
            }
            compareResult = this.tryCompareByPartialMatch(pattern, aDescription, bDescription);
            if (compareResult.success) {
                return compareResult.result;
            }
            return aDisplayName.localeCompare(bDisplayName);
        }
        else {
            return aDisplayName.localeCompare(bDisplayName);
        }
    }
    tryCompareByFullMatch(pattern, a, b) {
        const aIsFullMatch = a === pattern;
        const bIsFullMatch = b === pattern;
        const result = Number(bIsFullMatch) - Number(aIsFullMatch);
        const success = (result === 0 && aIsFullMatch) || result !== 0;
        return { success, result };
    }
    tryCompareByPartialMatch(pattern, a, b) {
        const aIsStartWith = a ? a.startsWith(pattern) : false;
        const bIsStartWith = b ? b.startsWith(pattern) : false;
        let result = Number(bIsStartWith) - Number(aIsStartWith);
        if (result === 0 && aIsStartWith) {
            result = a.localeCompare(b);
            return { success: true, result };
        }
        let aIsContains = false;
        if (result === 0) {
            aIsContains = a && a.includes(pattern);
            const bIsContains = b && b.includes(pattern);
            result = Number(bIsContains) - Number(aIsContains);
        }
        if (result === 0 && aIsContains) {
            result = a.localeCompare(b);
            return { success: true, result };
        }
        return { success: result !== 0, result };
    }
    getInstrumentDisplayName(instrument) {
        if (instrument == null) {
            return '';
        }
        let displayName;
        if (instrument.InstrumentSpecificType === InstrumentSpecificType.ContinuousContract) {
            displayName = instrument.ContinuousContractName;
        }
        else {
            displayName = instrument.DisplayName();
        }
        return isValidString(displayName) ? displayName.toLowerCase() : '';
    }
    getInstrumentDescription(instrument) {
        if (instrument == null) {
            return '';
        }
        return isValidString(instrument.Description) ? instrument.Description.toLowerCase() : '';
    }
}
export const InstrumentLookupComparer = new _InstrumentLookupComparer();
